import React, { useState, useMemo } from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Divider,
  Progress,
  Badge,
  Input,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { useDebugStore } from '../../../../stores/debugStore';
import styles from '../PersonalizationDebugPanel.module.css';

const SkillTracker: React.FC = () => {
  const { userSkills, skillStatistics } = useDebugStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [sortBy, setSortBy] = useState('proficiency');
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Filter and sort skills
  const filteredSkills = useMemo(() => {
    let filtered = userSkills;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(skill =>
        skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skill.skill_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Level filter
    if (filterLevel !== 'all') {
      filtered = filtered.filter(skill => {
        const proficiency = skill.proficiency;
        switch (filterLevel) {
          case 'mastered':
            return proficiency >= 0.8;
          case 'learning':
            return proficiency >= 0.4 && proficiency < 0.8;
          case 'struggling':
            return proficiency < 0.4;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'proficiency':
          return b.proficiency - a.proficiency;
        case 'confidence':
          return b.confidence - a.confidence;
        case 'recent':
          return (b.last_practiced || '').localeCompare(a.last_practiced || '');
        case 'attempts':
          return (b.total_attempts || 0) - (a.total_attempts || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [userSkills, searchTerm, filterLevel, sortBy]);

  const getProficiencyColor = (proficiency: number) => {
    if (proficiency >= 0.8) return 'green';
    if (proficiency >= 0.4) return 'yellow';
    return 'red';
  };

  const getProficiencyLabel = (proficiency: number) => {
    if (proficiency >= 0.8) return 'Mastered';
    if (proficiency >= 0.6) return 'Proficient';
    if (proficiency >= 0.4) return 'Learning';
    if (proficiency >= 0.2) return 'Developing';
    return 'Beginner';
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="md">Skill Tracker</Heading>
      
      {/* Statistics */}
      {skillStatistics && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Total</StatLabel>
            <StatNumber fontSize="2xl">{skillStatistics.total_tracked || 0}</StatNumber>
            <StatHelpText fontSize="xs">Skills tracked</StatHelpText>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Mastered</StatLabel>
            <StatNumber fontSize="2xl" color="green.500">
              {skillStatistics.mastered || 0}
            </StatNumber>
            <StatHelpText fontSize="xs">≥80% proficiency</StatHelpText>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Learning</StatLabel>
            <StatNumber fontSize="2xl" color="yellow.500">
              {skillStatistics.learning || 0}
            </StatNumber>
            <StatHelpText fontSize="xs">40-79% proficiency</StatHelpText>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Struggling</StatLabel>
            <StatNumber fontSize="2xl" color="red.500">
              {skillStatistics.struggling || 0}
            </StatNumber>
            <StatHelpText fontSize="xs">&lt;40% proficiency</StatHelpText>
          </Stat>
        </SimpleGrid>
      )}

      <Divider />

      {/* Filters */}
      <HStack spacing={3}>
        <Input
          placeholder="Search skills..."
          size="sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select
          size="sm"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          minW="120px"
        >
          <option value="all">All Levels</option>
          <option value="mastered">Mastered</option>
          <option value="learning">Learning</option>
          <option value="struggling">Struggling</option>
        </Select>
        <Select
          size="sm"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          minW="120px"
        >
          <option value="proficiency">Proficiency</option>
          <option value="confidence">Confidence</option>
          <option value="recent">Recent</option>
          <option value="attempts">Attempts</option>
        </Select>
      </HStack>

      {/* Skills List */}
      {filteredSkills.length === 0 ? (
        <Box
          p={8}
          textAlign="center"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          borderStyle="dashed"
        >
          <Text color="gray.500">
            {userSkills.length === 0 ? 'No skills tracked yet' : 'No skills match filters'}
          </Text>
        </Box>
      ) : (
        <Accordion allowMultiple>
          {filteredSkills.map((skill) => (
            <AccordionItem key={skill.skill_id}>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">
                          {skill.skill_name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {skill.skill_code}
                        </Text>
                      </VStack>
                      <Badge
                        colorScheme={getProficiencyColor(skill.proficiency)}
                        variant="subtle"
                      >
                        {getProficiencyLabel(skill.proficiency)}
                      </Badge>
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.600">Proficiency</Text>
                      <Text fontSize="xs" fontWeight="medium">
                        {Math.round(skill.proficiency * 100)}%
                      </Text>
                    </HStack>
                    <Progress
                      value={skill.proficiency * 100}
                      size="sm"
                      colorScheme={getProficiencyColor(skill.proficiency)}
                      borderRadius="full"
                    />
                  </Box>
                  
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.600">Confidence</Text>
                      <Text fontSize="xs" fontWeight="medium">
                        {Math.round(skill.confidence * 100)}%
                      </Text>
                    </HStack>
                    <Progress
                      value={skill.confidence * 100}
                      size="sm"
                      colorScheme="purple"
                      borderRadius="full"
                    />
                  </Box>

                  <SimpleGrid columns={2} spacing={2}>
                    {skill.total_attempts !== undefined && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Total Attempts</Text>
                        <Text fontSize="sm" fontWeight="medium">{skill.total_attempts}</Text>
                      </Box>
                    )}
                    {skill.successful_attempts !== undefined && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Successful</Text>
                        <Text fontSize="sm" fontWeight="medium">{skill.successful_attempts}</Text>
                      </Box>
                    )}
                    {skill.last_practiced && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Last Practiced</Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {new Date(skill.last_practiced).toLocaleDateString()}
                        </Text>
                      </Box>
                    )}
                    {skill.successful_attempts !== undefined && skill.total_attempts !== undefined && skill.total_attempts > 0 && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Success Rate</Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {Math.round((skill.successful_attempts / skill.total_attempts) * 100)}%
                        </Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </VStack>
  );
};

export default SkillTracker;